import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import StarIcon from '@mui/icons-material/Star';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Grid, Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { useAuth } from '../../contexts/AuthContext';

const MetricCard = styled(Box)(({ theme }) => ({
  padding: '1.5rem',
  borderRadius: '0.75rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  backgroundColor: '#ffffff',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  borderRadius: '50%',
  backgroundColor: 'rgba(0, 0, 0, 0.04)',
  marginBottom: '1rem',
}));

// Define a type for metrics with optional details
interface Metric {
  title: string;
  value: string | number;
  change: string;
  period: string;
  trend: string;
  color: string;
  icon: React.ReactNode;
  details?: string[];
}

const MetricsPanel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated } = useAuth();

  // Document analytics state
  const [docUsage, setDocUsage] = useState<any>(null);
  const [docRisk, setDocRisk] = useState<any>(null);
  const [docValue, setDocValue] = useState<any[]>([]);

  useEffect(() => {
    if (isAuthenticated) {
      // Add 500ms delay to ensure token is fully stored and available
      const timer = setTimeout(() => {
        // Fetch document analytics
        axios.get('/api/dashboard/document-analytics').then(r => setDocUsage(r.data));
        axios.get('/api/dashboard/document-risk').then(r => setDocRisk(r.data));
        axios.get('/api/dashboard/document-value').then(r => setDocValue(r.data));
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  // Update metrics and docCards to use Metric type
  const metrics: Metric[] = [
    {
      title: 'Active Cases',
      value: '24',
      change: '+4%',
      period: 'from last month',
      trend: 'up',
      color: '#D4AF37',
      icon: <FolderOutlinedIcon />,
    },
    {
      title: 'Win Rate',
      value: '87%',
      change: '+2.5%',
      period: 'from last quarter',
      trend: 'up',
      color: '#D4AF37',
      icon: <EmojiEventsOutlinedIcon />,
    },
    {
      title: 'Billable Hours',
      value: '164',
      change: '+12 hours',
      period: 'this week',
      trend: 'up',
      color: '#D4AF37',
      icon: <AccessTimeOutlinedIcon />,
    },
    {
      title: 'Revenue',
      value: '$42.5K',
      change: '+8%',
      period: 'from last month',
      trend: 'up',
      color: '#D4AF37',
      icon: <AttachMoneyOutlinedIcon />,
    },
  ];

  const docCards: Metric[] = [
    docUsage && {
      title: 'Document Usage',
      value: docUsage.total,
      change: `+${docUsage.uploads} uploads`,
      period: 'this month',
      trend: 'up',
      color: '#1976d2',
      icon: <BarChartIcon />,
      details: [
        `Edits: ${docUsage.edits}`,
        `Views: ${docUsage.views}`,
        `Shares: ${docUsage.shares}`,
      ],
    },
    docRisk && {
      title: 'Risk & Compliance',
      value: docRisk.confidential,
      change: `${docRisk.auditIssues} issues`,
      period: 'expiring soon',
      trend: docRisk.auditIssues > 0 ? 'down' : 'up',
      color: '#d32f2f',
      icon: <SecurityIcon />,
      details: [`Expiring: ${docRisk.expiring}`, `Audit Issues: ${docRisk.auditIssues}`],
    },
    docValue &&
      docValue.length > 0 && {
        title: 'Document Value',
        value: `${Math.round((docValue.reduce((a, d) => a + d.confidence, 0) / docValue.length) * 100)}%`,
        change: `${docValue.length} scored`,
        period: 'AI confidence',
        trend: 'up',
        color: '#388e3c',
        icon: <StarIcon />,
        details: [
          `Avg Usage: ${Math.round(docValue.reduce((a, d) => a + d.usage, 0) / docValue.length)}`,
        ],
      },
  ].filter(Boolean);

  return (
    <Grid container spacing={isMobile ? 2 : 3}>
      {metrics.map((metric, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <MetricCard>
            <IconContainer>
              <Box sx={{ color: metric.color }}>{metric.icon}</Box>
            </IconContainer>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {metric.value}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {metric.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {metric.trend === 'up' ? (
                <TrendingUpIcon sx={{ color: '#10b981', fontSize: '1rem', mr: 0.5 }} />
              ) : (
                <TrendingDownIcon sx={{ color: '#ef4444', fontSize: '1rem', mr: 0.5 }} />
              )}
              <Typography
                variant="caption"
                color={metric.trend === 'up' ? '#10b981' : '#ef4444'}
                sx={{ fontWeight: 'medium', mr: 0.5 }}
              >
                {metric.change}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {metric.period}
              </Typography>
            </Box>
            {/* Show details for document analytics */}
            {metric.details && (
              <Box mt={1}>
                {metric.details.map((d: string, i: number) => (
                  <Typography key={i} variant="caption" color="text.secondary" display="block">
                    {d}
                  </Typography>
                ))}
              </Box>
            )}
          </MetricCard>
        </Grid>
      ))}
      {/* Document analytics cards */}
      {docCards.map((metric, index) => (
        <Grid item xs={12} sm={6} md={3} key={`doc-${index}`}>
          <MetricCard>
            <IconContainer>
              <Box sx={{ color: metric.color }}>{metric.icon}</Box>
            </IconContainer>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {metric.value}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {metric.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {metric.trend === 'up' ? (
                <TrendingUpIcon sx={{ color: '#10b981', fontSize: '1rem', mr: 0.5 }} />
              ) : (
                <TrendingDownIcon sx={{ color: '#ef4444', fontSize: '1rem', mr: 0.5 }} />
              )}
              <Typography
                variant="caption"
                color={metric.trend === 'up' ? '#10b981' : '#ef4444'}
                sx={{ fontWeight: 'medium', mr: 0.5 }}
              >
                {metric.change}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {metric.period}
              </Typography>
            </Box>
            {metric.details && (
              <Box mt={1}>
                {metric.details.map((d: string, i: number) => (
                  <Typography key={i} variant="caption" color="text.secondary" display="block">
                    {d}
                  </Typography>
                ))}
              </Box>
            )}
          </MetricCard>
        </Grid>
      ))}
    </Grid>
  );
};

export default MetricsPanel;
